import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DynamicImage } from '@/components/ui/DynamicImage';
import { useAuth } from "@/contexts/AuthContext";
import { Heart } from "lucide-react";
import { useLanguage } from "@/lib/lang";
import { t } from "@/lib/i18n";
import { dataUnavailableAlert } from "@/lib/engagementUi";
import { buildPriEntityId, type PriPosition } from "@/lib/profileEntityId";
import { BroadShareArrow } from "@/components/ui/BroadShareArrow";
import { useProfileEngagement } from "@/hooks/useProfileEngagement";
import { AnchoredPopup } from "@/components/gram-panchayat/EminentPersonDisplay";

interface LeaderProfileCardProps {
  id: string;
  engagementType?: string;
  engagementKey?: string;
  district?: string;
  block?: string;
  village?: string;
  level?: "gp" | "bs" | "zp";
  tenure?: "current" | "previous";
  position?: PriPosition;
  wardNumber?: number;
  entityIdOverride?: string;
  engagementDisabled?: boolean;
  imageSrc?: string; // Static image source
  primarySrc?: string; // For DynamicImage
  fallbackSrc?: string; // For DynamicImage
  alt: string;
  altKey?: string; // For DynamicImage
  name: string | React.ReactNode;
  designation: string;
  className?: string;
  imgClassName?: string;
  // Optional children to render custom content (like the WhatsApp button for Sarpanch)
  children?: React.ReactNode;
  // Custom image renderer if needed (e.g. for Sarpanch complex image wrapper)
  customImageRenderer?: () => React.ReactNode;
}

export const LeaderProfileCard: React.FC<LeaderProfileCardProps> = ({
  id,
  engagementType = "profile",
  engagementKey,
  district,
  block,
  village,
  level,
  tenure = "current",
  position,
  wardNumber,
  entityIdOverride,
  engagementDisabled,
  imageSrc,
  primarySrc,
  fallbackSrc,
  alt,
  altKey,
  name,
  designation,
  className = "",
  imgClassName = "",
  children,
  customImageRenderer
}) => {
  const { user } = useAuth()
  console.log("CARD USER:", user?.id)
  const { lang } = useLanguage()
  const baseKey = engagementKey ?? id
  const cleanId = String(baseKey).replace("profile::", "").replace("ward::", "")

  const resolvedPosition: PriPosition = useMemo(() => {
    if (position) return position
    if (engagementType === "ward") return "ward-member"
    if (String(id).includes("vice")) return "vice-chairperson"
    if (level === "gp") return "sarpanch"
    return "chairperson"
  }, [engagementType, id, level, position])

  const entityId = useMemo(() => {
    if (entityIdOverride) return entityIdOverride
    if (!level) return cleanId
    return buildPriEntityId({
      level,
      tenure,
      position: resolvedPosition,
      district,
      block,
      village,
      wardNumber,
      personKey: cleanId || id,
    })
  }, [block, cleanId, district, entityIdOverride, id, level, resolvedPosition, tenure, village, wardNumber])

  const showEngagementButtons = true

  useEffect(() => {
    console.log("ENTITY ID:", entityId)
    if (!level) return
    if (!entityId) {
      const hasAnyLocation = Boolean(String(district || "").trim() || String(block || "").trim() || String(village || "").trim())
      if (hasAnyLocation) {
        console.error("INVALID LEADER ID PARTS:", { level, tenure, resolvedPosition, district, block, village, wardNumber })
      }
    }
  }, [block, district, entityId, level, resolvedPosition, tenure, village, wardNumber])

  const { like, share, likes, shares, hasLiked } = useProfileEngagement(entityId || "", user?.id)
  const isEngagementDisabled = Boolean(engagementDisabled) || !entityId
  const disabledAlert = dataUnavailableAlert(lang)
  const [optimisticLiked, setOptimisticLiked] = useState(false)
  const [optimisticShared, setOptimisticShared] = useState(false)
  const [likePulse, setLikePulse] = useState(false)
  const [sharePulse, setSharePulse] = useState(false)
  const likeButtonRef = useRef<HTMLButtonElement | null>(null)
  const loginPopupTimerRef = useRef<number | null>(null)
  const [showLoginPopup, setShowLoginPopup] = useState(false)

  useEffect(() => {
    return () => {
      if (loginPopupTimerRef.current != null) {
        globalThis.clearTimeout(loginPopupTimerRef.current)
        loginPopupTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    setOptimisticLiked(hasLiked)
  }, [hasLiked])

  const likeButtonClassName = isEngagementDisabled
    ? "group relative inline-flex items-center gap-1.5 rounded-xl border border-white/70 bg-slate-200/80 backdrop-blur px-2.5 py-2 shadow-sm opacity-70 cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-600"
    : "group relative inline-flex items-center gap-1.5 rounded-xl border border-emerald-600/25 bg-white/85 backdrop-blur px-2.5 py-2 shadow-sm transition-all hover:shadow-md active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#064E3B]"
  const shareButtonClassName = isEngagementDisabled
    ? "group relative inline-flex items-center gap-1.5 rounded-xl border border-white/70 bg-slate-200/80 backdrop-blur px-2.5 py-2 shadow-sm opacity-70 cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-600"
    : "group relative inline-flex items-center gap-1.5 rounded-xl border border-emerald-600/25 bg-white/85 backdrop-blur px-2.5 py-2 shadow-sm transition-all hover:shadow-md active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#064E3B]"

  return (
    <figure data-engagement-type={engagementType} className={`w-full max-w-[200px] flex flex-col items-center relative isolate ${className}`}>
      {/* Image Section */}
      {customImageRenderer ? (
        customImageRenderer()
      ) : (
        imageSrc ? (
          <img
            src={imageSrc}
            alt={alt}
            data-alt-key={altKey}
            width={200}
            height={200}
            className={`w-full aspect-square object-cover rounded-md shadow border border-border transition-transform hover:scale-105 duration-300 ${imgClassName}`}
          />
        ) : (
          <DynamicImage
            primarySrc={primarySrc || ""}
            fallbackSrc={fallbackSrc || "/images/placeholder.svg"}
            alt={alt}
            altKey={altKey}
            width={200}
            height={200}
            className={`w-full aspect-square object-cover rounded-md shadow border border-border transition-transform hover:scale-105 duration-300 ${imgClassName}`}
          />
        )
      )}

      {/* Action Bar (Like/Share) */}
      <div className="relative z-20 flex items-center justify-center gap-3 mt-3 w-full pointer-events-auto">
        {showEngagementButtons && (
          <>
            <button
              ref={likeButtonRef}
              onClick={(e) => {
                if (isEngagementDisabled) {
                  e.preventDefault()
                  e.stopPropagation()
                  alert(disabledAlert)
                  return
                }
                if (!user?.id) {
                  e.preventDefault()
                  e.stopPropagation()
                  setShowLoginPopup(true)
                  if (loginPopupTimerRef.current != null) globalThis.clearTimeout(loginPopupTimerRef.current)
                  loginPopupTimerRef.current = globalThis.setTimeout(() => setShowLoginPopup(false), 3000) as unknown as number
                  return
                }
                setLikePulse(true)
                window.setTimeout(() => setLikePulse(false), 260)
                if (user?.id) setOptimisticLiked((v) => !v)
                console.log("LIKE CLICKED");
                like();
              }}
              className={likeButtonClassName}
              title="Like"
              aria-label="Like"
            >
              <Heart
                strokeWidth={2.2}
                className={[
                  "ward-engagement-icon transition-all duration-200 ease-in-out drop-shadow-sm",
                  "h-6 w-6 sm:h-5 sm:w-5",
                  optimisticLiked ? "text-[#FF0000] fill-[#FF0000]" : "text-emerald-600 fill-transparent",
                  likePulse ? "scale-105" : "",
                  "group-hover:drop-shadow-md",
                ].join(" ")}
              />
              <span className="text-sm font-semibold tabular-nums text-emerald-700">{likes}</span>
            </button>
            <AnchoredPopup open={showLoginPopup} anchorRef={likeButtonRef}>
              <div className="rounded-lg bg-slate-900/95 px-3 py-2 text-xs font-semibold text-white shadow-lg ring-1 ring-white/10">
                {t("login_required_like", lang)}
              </div>
            </AnchoredPopup>
            <button
              data-shared={optimisticShared ? "true" : undefined}
              onClick={async (e) => {
                if (isEngagementDisabled) {
                  e.preventDefault()
                  e.stopPropagation()
                  alert(disabledAlert)
                  return
                }
                setSharePulse(true)
                window.setTimeout(() => setSharePulse(false), 260)
                console.log("SHARE CLICKED");
                const ok = await share()
                if (ok) setOptimisticShared(true)
              }}
              className={`${shareButtonClassName} group`}
              title="Share"
              aria-label="Share"
            >
              <BroadShareArrow
                className={[
                  "ward-engagement-icon text-emerald-600 transition-all duration-200 ease-in-out drop-shadow-sm",
                  "h-6 w-6 sm:h-5 sm:w-5",
                  "group-data-[shared=true]:text-[#E60023]",
                  sharePulse ? "scale-105" : "",
                  "group-hover:drop-shadow-md",
                ].join(" ")}
              />
              <span className="text-sm font-semibold tabular-nums text-emerald-700">{shares}</span>
            </button>
          </>
        )}
      </div>

      <figcaption className="mt-2 w-full flex flex-col items-center gap-0">
        <span className="heading-tertiary text-center leading-tight px-0 w-full block whitespace-normal break-words">
          {name}
        </span>
        <span className="text-body text-sm text-center leading-tight px-0 w-full block whitespace-normal break-words text-muted-foreground">
          {designation}
        </span>
        {children}
      </figcaption>
    </figure>
  );
};
